import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Box, Button, CircularProgress, Paper, TextField, Typography } from '@mui/material';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { ThemeProvider } from '@mui/material/styles';
import CaseStatusLayout from './CaseStatusLayout';
import caseStatusTheme from '../../logic/components/CaseStatus/caseStatusTheme';
import GlobalConfig from '../../core/api/GlobalConfig';
import { CaseStatusApi } from '../../logic/components/CaseStatus/api/CaseStatusApi';
import CaseStatusLanguageProvider, { useCaseStatusLanguage } from './CaseStatusLanguageProvider';

declare global {
  // eslint-disable-next-line no-var
  var turnstile: {
    render: (container: string | HTMLElement, options: TurnstileOptions) => string;
    remove: (widgetId: string) => void;
    reset: (widgetId: string) => void;
  } | undefined;
  // eslint-disable-next-line no-var
  var onTurnstileLoad: (() => void) | undefined;
}

type TurnstileOptions = {
  sitekey: string;
  callback: (token: string) => void;
  'error-callback'?: () => void;
  'expired-callback'?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  language?: string;
};

const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script';

const CaseStatusVerificationContent = () => {
  const history = useHistory();
  const location = useLocation();
  const caseNumber = new URLSearchParams(location.search).get('caseNumber') ?? '';
  const widgetRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [zipCode, setZipCode] = useState('');
  const [zipError, setZipError] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const { t, culture } = useCaseStatusLanguage();
  const turnstileLanguage = culture.startsWith('ar') ? 'ar' : culture;

  const turnstileEnabled = GlobalConfig.val().MyPageCaseStatusTurnstileEnabled ?? true;
  const zipCodeEnabled = GlobalConfig.val().MyPageCaseStatusZipCodeEnabled ?? false;
  const siteKey = GlobalConfig.val().TurnstileSiteKey ?? '';

  const requestAccessToken = async (token: string | null) => {
    setSubmitting(true);
    try {
      const accessToken = await CaseStatusApi.getAccessToken(
        caseNumber,
        token ?? undefined,
        zipCodeEnabled ? zipCode.trim() : undefined
      );
      history.push({
        pathname: '/case-status',
        search: `?caseNumber=${encodeURIComponent(caseNumber)}`,
        state: { accessToken },
      });
    } catch {
      setError(true);
      setSubmitting(false);
      if (widgetIdRef.current && globalThis.turnstile) {
        globalThis.turnstile.reset(widgetIdRef.current);
      }
      setTurnstileToken(null);
    }
  };

  const onVerified = async (token: string) => {
    setError(false);
    // When a zip code is also required, defer submission until the user clicks Continue.
    if (zipCodeEnabled) {
      setTurnstileToken(token);
      return;
    }
    await requestAccessToken(token);
  };

  const onSubmit = async () => {
    if (zipCodeEnabled && zipCode.trim().length === 0) {
      setZipError(true);
      return;
    }
    setZipError(false);

    if (turnstileEnabled && !turnstileToken) {
      // Wait for the Turnstile challenge to complete before submitting.
      return;
    }

    await requestAccessToken(turnstileToken);
  };

  useEffect(() => {
    if (!caseNumber) {
      history.replace('/signin');
      return;
    }

    if (!turnstileEnabled) {
      return;
    }

    // Load Turnstile script once
    if (!document.getElementById(TURNSTILE_SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad&render=explicit';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    globalThis.onTurnstileLoad = () => setScriptLoaded(true);

    // If script already loaded (cached), trigger directly
    if (globalThis.turnstile) {
      setScriptLoaded(true);
    }

    return () => {
      if (widgetIdRef.current && globalThis.turnstile) {
        globalThis.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [caseNumber, turnstileEnabled]);

  useEffect(() => {
    if (!turnstileEnabled || !scriptLoaded || !widgetRef.current || !globalThis.turnstile) return;

    if (widgetIdRef.current) {
      globalThis.turnstile.remove(widgetIdRef.current);
      widgetIdRef.current = null;
    }

    widgetIdRef.current = globalThis.turnstile.render(widgetRef.current, {
      sitekey: siteKey,
      callback: onVerified,
      'error-callback': () => setError(true),
      'expired-callback': () => {
        setTurnstileToken(null);
        if (widgetIdRef.current && globalThis.turnstile) {
          globalThis.turnstile.reset(widgetIdRef.current);
        }
      },
      theme: 'light',
      language: turnstileLanguage,
    });
  }, [turnstileEnabled, scriptLoaded, turnstileLanguage]);

  return (
    <ThemeProvider theme={caseStatusTheme}>
      <CaseStatusLayout>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            py: 8,
            px: 2,
          }}
        >
          <Paper elevation={1} sx={{ width: '100%', maxWidth: 520, p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <ShieldOutlinedIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              <Typography variant="h6" component="h1" id="human-verification-heading" sx={{ fontWeight: 700 }}>
                {t('HumanVerification')}
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
              {t('HumanVerificationBody', caseNumber)}
            </Typography>

            <Box sx={{ minHeight: 24, mb: 1 }} aria-live="polite">
              {error && (
                <Typography variant="body2" color="error" sx={{ textAlign: 'center' }}>
                  {t('VerificationFailed')}
                </Typography>
              )}
            </Box>

            {zipCodeEnabled && (
              <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography
                  component="label"
                  htmlFor="zipCode"
                  sx={{
                    display: 'block',
                    fontSize: 13,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    color: 'var(--brand-color-1)',
                    mb: 0.75,
                    textAlign: 'center',
                  }}
                >
                  {t('ZipCodeLabel')}
                </Typography>
                <TextField
                  id="zipCode"
                  size="small"
                  placeholder={t('ZipCodePlaceholder')}
                  value={zipCode}
                  onChange={(e) => {
                    setZipCode(e.target.value);
                    if (zipError) setZipError(false);
                  }}
                  error={zipError}
                  helperText={zipError ? t('ZipCodeRequired') : ' '}
                  slotProps={{ htmlInput: { 'aria-label': t('ZipCodeLabel'), maxLength: 10, style: { textAlign: 'center' } } }}
                  sx={{ mb: 0.75, width: '100%', maxWidth: 300 }}
                />
              </Box>
            )}

            {turnstileEnabled && !scriptLoaded && !error && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                <CircularProgress size={28} />
              </Box>
            )}

            {turnstileEnabled && (
              <Box
                role="group"
                aria-labelledby="human-verification-heading"
                sx={{ minHeight: 66, mb: 2, display: 'flex', justifyContent: 'center' }}
              >
                <div ref={widgetRef} />
              </Box>
            )}

            {zipCodeEnabled && (
              <Button
                fullWidth
                variant="contained"
                onClick={onSubmit}
                disabled={
                  submitting ||
                  (turnstileEnabled && !turnstileToken) ||
                  (zipCodeEnabled && zipCode.trim().length === 0)
                }
                sx={{ mt: 1 }}
              >
                {submitting ? <CircularProgress size={22} color="inherit" /> : t('Continue')}
              </Button>
            )}
          </Paper>
        </Box>
      </CaseStatusLayout>
    </ThemeProvider>
  );
};

const CaseStatusVerification = () => (
  <CaseStatusLanguageProvider embedded>
    <CaseStatusVerificationContent />
  </CaseStatusLanguageProvider>
);

export default CaseStatusVerification;
